/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITag } from '@fluentui/react/lib/Pickers';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { TagContainer, TagContainerProps } from 'src/core/components/TagContainer';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { getStylist, joinClassNames } from 'src/core/themes/luisStylist';
import { renderTags, stringArrayToTags } from 'src/luis/modules/features/components/utils';

export type PhraseListTagsContainerProps = Omit<TagContainerProps<string>, 'selection'> & {
    /**
     * The string to display in the fieldset.
     */
    legend: string;

    /**
     * The items to display in the container.
     */
    items: string[];

    /**
     * Whether the items will show an add or
     * delete icon.
     */
    itemsMode: 'add' | 'remove';

    /**
     * Optional callback function handles onClick event.
     */
    onContainerClick?: () => void;

    /**
     * Indicates if this field is required.
     */
    isRequired?: boolean;

    /**
     * Whether the user can add, delete or perform any action in the container
     */
    areValuesEditable?: boolean;
};

const messages = defineMessages({
    removeButtonAriaLabel: {
        id: 'luis.phraseListTagsContainer.removeButtonAriaLabel',
        defaultMessage: 'Remove value'
    }
});

const { getClassName, styleDiv } = getStylist('PhraseListTagsContainer');

const fieldsetClassName = getClassName('Container', theme => ({
    border: theme.containerBorder,
    borderRadius: theme.containerBorderRadius,
    padding: theme.containerPadding
}));

const legendClassName = getClassName('Legend', theme => ({
    paddingLeft: theme.containerLegendPadding,
    paddingRight: theme.containerLegendPadding
}));

const requiredLegendClassName = getClassName('RequiredLegend', theme => ({
    $nest: {
        '&::after': {
            content: '" *"',
            color: theme.requiredIndicatorColor,
            fontWeight: 600
        }
    }
}));

const TagContainerHeightController = styleDiv('TagContainerHeightController', theme => ({
    height: theme.containerHeight,
    overflowY: 'auto',
    overflowX: 'hidden'
}));

/**
 * Represents a container for phrase list values.
 *
 * @param props The input passed to the container.
 */
export const PhraseListTagsContainer = (props: PhraseListTagsContainerProps) => {
    const { items, legend, onRemoveItems, itemsMode, children, onContainerClick, isRequired = false, areValuesEditable = true } = props;
    const { selection, selectedItems } = useSelection<ITag>();
    const tags = stringArrayToTags(items);
    const { localize } = useLocalization();

    React.useEffect(() => selection.setItems(tags, false), [items]);

    return (
        <fieldset className={fieldsetClassName}>
            <legend className={joinClassNames(legendClassName, isRequired ? requiredLegendClassName : '')}>{legend}</legend>

            <TagContainerHeightController onClick={onContainerClick}>
                <TagContainer<ITag>
                    selection={selection}
                    onRemoveItems={tagsToRemove => onRemoveItems && onRemoveItems(tagsToRemove.map(t => t.name))}>
                    {renderTags(
                        tags,
                        selectedItems,
                        onRemoveItems,
                        itemsMode,
                        localize(messages.removeButtonAriaLabel),
                        !areValuesEditable
                    )}
                    {children}
                </TagContainer>
            </TagContainerHeightController>
        </fieldset>
    );
};
